import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { CaretLeft, Bell, Check, ChatCircle, CurrencyDollar, WarningOctagon, ArrowsCounterClockwise, ShieldCheck, Briefcase, Flag, Tray, Funnel } from '@phosphor-icons/react';

const ICON_MAP = { counter_offer: ArrowsCounterClockwise, dispute: WarningOctagon, dispute_resolved: ShieldCheck, payment: CurrencyDollar, chat: ChatCircle, job_match: Briefcase, milestone: Flag, milestone_approved: Flag, default: Bell };
const COLOR_MAP = { counter_offer: 'text-[#F4A261] bg-[#F4A261]/10', dispute: 'text-red-500 bg-red-50', dispute_resolved: 'text-[#2D6A4F] bg-[#2D6A4F]/10', payment: 'text-[#E86A33] bg-[#E86A33]/10', job_match: 'text-[#1A4D2E] bg-[#1A4D2E]/10', milestone: 'text-[#F4A261] bg-[#F4A261]/10', milestone_approved: 'text-[#2D6A4F] bg-[#2D6A4F]/10', default: 'text-[#5C5C5C] bg-[#E8DDD2]' };

export default function NotificationsPage() {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get('/notifications');
        setNotifs(data.notifications || []);
        setUnread(data.unread_count || 0);
      } catch {} finally { setLoading(false); }
    };
    load();
    const interval = setInterval(load, 8000);
    return () => clearInterval(interval);
  }, [api]);

  const markAllRead = async () => {
    try { await api.post('/notifications/read-all'); setNotifs(n => n.map(x => ({ ...x, read: true }))); setUnread(0); } catch {}
  };

  const markRead = async (id) => {
    try { await api.post(`/notifications/${id}/read`); setNotifs(n => n.map(x => x.id === id ? { ...x, read: true } : x)); setUnread(u => Math.max(0, u - 1)); } catch {}
  };

  const filtered = filter === 'all' ? notifs : filter === 'unread' ? notifs.filter(n => !n.read) : notifs.filter(n => n.type === filter);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#E8DDD2] to-[#D6C9B8] pb-24 max-w-[480px] mx-auto" data-testid="notifications-page">
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-black/5">
        <div className="px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="w-9 h-9 bg-[#E8DDD2] rounded-full flex items-center justify-center"><CaretLeft size={18} /></button>
          <div className="flex-1">
            <h1 className="font-heading text-lg font-bold text-[#1C1C1C]">Inbox</h1>
            <p className="text-[10px] text-[#5C5C5C]">{unread} unread message{unread !== 1 ? 's' : ''}</p>
          </div>
          {unread > 0 && (
            <button onClick={markAllRead} className="text-xs font-bold text-[#E86A33] bg-[#E86A33]/10 px-3 py-1.5 rounded-full flex items-center gap-1" data-testid="mark-all-read"><Check size={12} /> Read all</button>
          )}
        </div>
        {/* Filters */}
        <div className="flex gap-2 px-4 pb-3 overflow-x-auto scrollbar-hide">
          {[{id:'all',label:'All'},{id:'unread',label:'Unread'},{id:'job_match',label:'Job Matches'},{id:'counter_offer',label:'Offers'},{id:'dispute',label:'Disputes'},{id:'milestone',label:'Milestones'}].map(f => (
            <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`inbox-filter-${f.id}`}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-bold transition-all ${filter === f.id ? 'bg-[#1A4D2E]/10 text-[#1A4D2E] border border-[#1A4D2E]' : 'bg-[#E8DDD2] text-[#5C5C5C] border border-transparent'}`}>
              {f.label}
            </button>
          ))}
        </div>
      </header>

      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-3 border-[#D4C5B3] border-t-[#E86A33] rounded-full animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <Tray size={48} className="text-[#E5E3DB] mx-auto mb-4" />
            <h3 className="font-heading text-lg font-bold text-[#1C1C1C] mb-2">{filter === 'unread' ? 'All caught up!' : 'No messages'}</h3>
            <p className="text-sm text-[#5C5C5C] font-body">
              {filter === 'unread' ? "You've read all your notifications." : 'Notifications will appear here in real-time.'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map(n => {
              const Icon = ICON_MAP[n.type] || ICON_MAP.default;
              const color = COLOR_MAP[n.type] || COLOR_MAP.default;
              return (
                <div key={n.id}
                  onClick={() => { markRead(n.id); if (n.job_id) navigate(`/job/${n.job_id}`); }}
                  className={`bg-white border rounded-xl p-4 flex items-start gap-3 cursor-pointer hover:-translate-y-0.5 transition-all duration-200 ${!n.read ? 'border-[#E86A33]/40 shadow-sm shadow-[#E86A33]/10' : 'border-[#D4C5B3]'}`}
                  data-testid={`inbox-item-${n.id}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${color}`}>
                    <Icon size={18} weight="duotone" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h4 className={`text-sm font-body truncate ${!n.read ? 'font-bold text-[#1C1C1C]' : 'font-semibold text-[#5C5C5C]'}`}>{n.title}</h4>
                      {!n.read && <div className="w-2.5 h-2.5 bg-[#E86A33] rounded-full flex-shrink-0 animate-pulse" />}
                    </div>
                    <p className="text-xs text-[#5C5C5C] font-body mt-0.5 line-clamp-2">{n.message}</p>
                    <p className="text-[10px] text-[#5C5C5C]/60 mt-1.5">{timeAgo(n.created_at)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}
